import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import {
  useHasSettingsEnabled,
  useShouldHideLegacySettings,
} from '~/lib/navigationPermissions';
import { useUser } from '~/modules/auth/hooks/useUser';
import { RemoteRouter } from '~/modules/remotes/router';

import { ErrorPage } from './components/ErrorPage';
import { App as DdexConsoleApp } from './modules/ddex-console/App';
import { FEATURE_FLAGS } from './modules/experiments/features';
import { useFeatureFlags } from './modules/experiments/hooks/useFeatureFlags';
import { App as LabelPerfApp } from './modules/label-perf/App';
import { CatalogApp } from './modules/label-perf/catalog/CatalogApp';
import { App as MarketingApp } from './modules/marketing/App/App';
import { App as ProfileApp } from './modules/my-profile/App';
import { PrimaryLayout } from './modules/navigation/components/PrimaryLayout';

const RosterApp = lazy(() => import('./modules/artist-roster/App/App'));

const UserManagementApp = lazy(
  () => import('./modules/user-management/App/App'),
);

const SettingsApp = lazy(() => import('./modules/settings/App'));

/**
 * Top-level router combining module-level routers
 */
function Router() {
  const { features } = useUser();
  const { hasPermission } = useUser();
  const canAccessSettings =
    useHasSettingsEnabled(features) && hasPermission('USER.VIEW');
  const shouldHideLegacySettings =
    !hasPermission('PLATFORM.SETTINGS') ||
    useShouldHideLegacySettings(features);

  const [profileEnabled] = useFeatureFlags([FEATURE_FLAGS.ContainerProfile]);

  return (
    <Routes>
      <Route element={<PrimaryLayout />}>
        <Route path="*" element={<RemoteRouter />} />
        <Route path="catalog/*" element={<CatalogApp />} />
        <Route path="label/*" element={<LabelPerfApp />} />
        <Route path="marketing/*" element={<MarketingApp />} />
        <Route path="release/ddex/*" element={<DdexConsoleApp />} />
        <Route path="roster/*" element={<RosterApp />} />
        <Route path="users/*" element={<UserManagementApp />} />

        {shouldHideLegacySettings ? (
          <Route
            path="platform/settings/*"
            element={<ErrorPage type="noAccessPage" />}
          />
        ) : null}

        <Route
          path="settings/*"
          element={
            canAccessSettings ? (
              <SettingsApp />
            ) : (
              <ErrorPage type="noAccessPage" />
            )
          }
        />
        {canAccessSettings && profileEnabled ? (
          <Route path="my-profile/*" element={<ProfileApp />} />
        ) : null}
      </Route>
    </Routes>
  );
}

Router.displayName = 'Router';

export { Router };
